import { useRef, useState } from 'react';
import { poolOrderApi } from '../services/poolOrderApi';

interface UploadPoolOrderModalProps {
  onClose: () => void;
  onIngested: (id: string, parseStatus: 'complete' | 'failed') => void;
}

export function UploadPoolOrderModal({ onClose, onIngested }: UploadPoolOrderModalProps) {
  const appRef   = useRef<HTMLInputElement>(null);
  const namesRef = useRef<HTMLInputElement>(null);
  const [docketOverride, setDocketOverride] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    const appFile = appRef.current?.files?.[0];
    if (!appFile) { setError('Application PDF is required.'); return; }
    const namesFile = namesRef.current?.files?.[0];

    const fd = new FormData();
    fd.append('application', appFile);
    if (namesFile) fd.append('names', namesFile);
    if (docketOverride.trim()) fd.append('docket_number_override', docketOverride.trim());
    if (notes.trim())          fd.append('notes', notes.trim());

    setSubmitting(true);
    try {
      const r = await poolOrderApi.upload(fd);
      onIngested(r.pool_order_id, r.parse_status);
    } catch (e: any) {
      setError(e?.message ?? 'Upload failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="bg-bg-card border border-border rounded-lg w-[640px] max-w-[92vw] max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">workspace</div>
            <div className="font-display text-[22px] leading-tight">Upload <em className="text-accent not-italic">pool order</em></div>
          </div>
          <button onClick={onClose} className="text-text-dim hover:text-text text-2xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="bg-bg-input/40 border border-border rounded px-3 py-2 text-[12px] text-text-dim">
            ECMC pooling dockets typically arrive as two PDFs: the Application and the Interested Parties list.
            Either PDF can carry the docket number; if Vision can't read it from either, fill the override below.
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-mono text-text-faint mb-1.5">
              Application PDF <span className="lowercase text-red/80">required</span>
            </label>
            <input ref={appRef} type="file" accept="application/pdf"
              className="w-full text-[12px] text-text-dim file:mr-3 file:px-3 file:py-1.5 file:rounded file:border file:border-border file:bg-bg-input file:text-text-dim hover:file:bg-bg-hover" />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-mono text-text-faint mb-1.5">
              Interested Parties PDF <span className="lowercase text-text-ghost">optional</span>
            </label>
            <input ref={namesRef} type="file" accept="application/pdf"
              className="w-full text-[12px] text-text-dim file:mr-3 file:px-3 file:py-1.5 file:rounded file:border file:border-border file:bg-bg-input file:text-text-dim hover:file:bg-bg-hover" />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-mono text-text-faint mb-1.5">
              Docket override <span className="lowercase text-text-ghost">if Vision can't read it</span>
            </label>
            <input value={docketOverride} onChange={e => setDocketOverride(e.target.value)}
              placeholder="e.g. 260400104"
              className="w-full bg-bg-input border border-border px-3 py-2 rounded text-[13px] font-mono text-text outline-none focus:border-accent" />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-mono text-text-faint mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full bg-bg-input border border-border px-3 py-2 rounded text-[13px] text-text outline-none focus:border-accent" />
          </div>

          {submitting && (
            <div className="text-[12px] text-accent-bright bg-accent/10 border border-accent/30 rounded px-3 py-2">
              Uploading + parsing PDF with Claude Vision… this takes 10–20 seconds. Don't close this window.
            </div>
          )}
          {error && (
            <div className="text-[12px] text-red bg-red/10 border border-red/40 rounded px-3 py-2">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 bg-bg-card-soft/40">
          <button onClick={onClose} disabled={submitting}
            className="px-4 py-2 text-[13px] text-text-dim hover:text-text border border-border rounded">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting}
            className="px-4 py-2 text-[13px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
            {submitting ? 'Parsing…' : 'Upload + parse'}
          </button>
        </div>
      </div>
    </div>
  );
}
